
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { API_CONFIG } from "@/constants/env";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList, Mail, User } from "lucide-react";
import {GlowBackground} from "@/components/GlowBackground.tsx";
import { SuccessModal } from "@/components/SuccessModal";
import { collectBrowserInfo, type BrowserInfo } from "@/utils/browserInfo";
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { trackEvent, trackTiming } from '@/utils/analytics';
import SEOHelmet from '@/components/SEOHelmet';

const formSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  company: z.string().min(1, "Company name is required"),
  industry: z.string().min(1, "Industry is required"),
  company_size: z.string().min(1, "Company size is required"),
  compliance_needs: z.string().min(1, "Compliance needs are required"),
  process_requirements: z.string().min(1, "Process requirements are required"),
  additional_info: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const BookDemoPage = () => {
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language;
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    // Set document title
    document.title = t("bookDemo.title");
    
    // Check if running on localhost
    const hostname = window.location.hostname;
    setIsLocalhost(hostname === "localhost" || hostname === "127.0.0.1");
  }, [t]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      company: "",
      industry: "",
      company_size: "",
      compliance_needs: "",
      process_requirements: "",
      additional_info: "",
    },
  });

  // Set fake data for development environment
  useEffect(() => {
    if (isLocalhost) {
      form.reset({
        first_name: "John",
        last_name: "Doe",
        email: "john.doe@example.com",
        company: "Acme Corporation",
        industry: "Healthcare",
        company_size: "100-500",
        compliance_needs: "ESG Reporting, GDPR Compliance",
        process_requirements: "Need to automate our regulatory reporting process and improve data quality checks.",
        additional_info: "I'm interested in learning more about your product. Please contact me to schedule a demo.",
      });
    }
  }, [isLocalhost, form]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Collect browser and device information
    const browserInfo = collectBrowserInfo();
    
    // Prepare submission data with browser info
    const submissionData = {
      ...data,
      browser_info: browserInfo
    };
    
    // Track the form submission attempt with relevant data
    trackEvent('BookDemo', 'FormSubmit', 'DemoForm', {
      industry: data.industry,
      companySize: data.company_size,
      complianceNeeds: data.compliance_needs
    });
    
    // Start timing for form submission
    const startTime = performance.now();
    
    try {
      const response = await fetch(`${API_CONFIG.API_BASE_URL}/book-demo/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        // Handle non-200 responses without trying to parse them as JSON
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Check if the response has content before trying to parse it
      const contentType = response.headers.get("content-type");
      let responseData;
      
      if (contentType && contentType.includes("application/json") && response.bodyUsed === false) {
        // Only try to parse JSON if the content type is application/json and body hasn't been read
        const text = await response.text();
        responseData = text ? JSON.parse(text) : {};
      }

      // Track successful form submission
      trackEvent('BookDemo', 'FormSubmitSuccess', 'DemoForm');

      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      let errorMessage = t("bookDemo.notifications.error.description");
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: t("bookDemo.notifications.error.title"),
        description: errorMessage,
        variant: "destructive",
      });
      
      // Track form submission error
      trackEvent('BookDemo', 'FormSubmitError', errorMessage);
      
      setIsSubmitting(false);
    } finally {
      // Track timing for form submission
      const endTime = performance.now();
      const timeTaken = Math.round(endTime - startTime);
      trackTiming('BookDemo', 'FormSubmission', timeTaken, 'DemoForm');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHelmet path="/book-demo" />
      <PageViewTracker path="/book-demo" title="Book a Demo - Verdea AI Compliance Platform" />
      <NavBar />
      <GlowBackground colorScheme={'green'} />

      <div className="container mx-auto px-4 pt-24 pb-16 flex-grow relative z-10">
        {/* Background Elements - Green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Dark green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background: 'radial-gradient(circle at center, rgba(24, 76, 6, 0.3) 0%, rgba(24, 76, 6, 0) 70%)', // Dark green #184c06
              transform: 'translate(20%, -20%)'
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background: 'radial-gradient(circle at center, rgba(34, 147, 2, 0.3) 0%, rgba(34, 147, 2, 0) 70%)', // Light green #229302
              transform: 'translate(-20%, 20%)'
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(34, 147, 2, 0.2) 1px, transparent 1px)', // Light green #229302
              backgroundSize: '40px 40px'
            }}
          />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-12 text-center">
            <Badge
              variant="outline"
              className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400"
            >
              {t("bookDemo.badge") || "Schedule a Demo"}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              {t("bookDemo.title").split(" ").slice(0, -2).join(" ")}
              <br />
              <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">
                {t("bookDemo.title").split(" ").slice(-2).join(" ")}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t("bookDemo.subtitle")}
            </p>
          </div>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-8 ${showSuccessModal ? 'pointer-events-none opacity-75' : ''}`}>
              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <User className="h-5 w-5 text-green-600" />
                  {t("bookDemo.personalInfo")}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("bookDemo.firstName")}</FormLabel>
                        <FormControl>
                          <Input placeholder={t("bookDemo.placeholders.firstName")} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("bookDemo.lastName")}</FormLabel>
                        <FormControl>
                          <Input placeholder={t("bookDemo.placeholders.lastName")} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("bookDemo.email")}</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder={t("bookDemo.placeholders.email")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("bookDemo.company")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("bookDemo.placeholders.company")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("bookDemo.industry")}</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            field.onChange(value);
                            trackEvent('BookDemo', 'IndustrySelect', value);
                          }} 
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t("bookDemo.placeholders.industry")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="banking">{t("bookDemo.industries.banking")}</SelectItem>
                            <SelectItem value="healthcare">{t("bookDemo.industries.healthcare")}</SelectItem>
                            <SelectItem value="insurance">{t("bookDemo.industries.insurance")}</SelectItem>
                            <SelectItem value="manufacturing">{t("bookDemo.industries.manufacturing")}</SelectItem>
                            <SelectItem value="retail">{t("bookDemo.industries.retail")}</SelectItem>
                            <SelectItem value="technology">{t("bookDemo.industries.technology")}</SelectItem>
                            <SelectItem value="energy">{t("bookDemo.industries.energy")}</SelectItem>
                            <SelectItem value="real_estate">{t("bookDemo.industries.realEstate")}</SelectItem>
                            <SelectItem value="other">{t("bookDemo.industries.other")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="company_size"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("bookDemo.companySize")}</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            field.onChange(value);
                            trackEvent('BookDemo', 'CompanySizeSelect', value);
                          }} 
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t("bookDemo.placeholders.companySize")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1-50">{t("bookDemo.companySizes.small")}</SelectItem>
                            <SelectItem value="51-200">{t("bookDemo.companySizes.medium")}</SelectItem>
                            <SelectItem value="201-500">{t("bookDemo.companySizes.mediumLarge")}</SelectItem>
                            <SelectItem value="501-1000">{t("bookDemo.companySizes.large")}</SelectItem>
                            <SelectItem value="1001-5000">{t("bookDemo.companySizes.veryLarge")}</SelectItem>
                            <SelectItem value="5000+">{t("bookDemo.companySizes.enterprise")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <ClipboardList className="h-5 w-5 text-green-600" />
                  {t("bookDemo.complianceRequirements")}
                </h2>
                <FormField
                  control={form.control}
                  name="compliance_needs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("bookDemo.complianceNeeds")}</FormLabel>
                      <Select 
                        onValueChange={(value) => {
                          field.onChange(value);
                          trackEvent('BookDemo', 'ComplianceNeedsSelect', value);
                        }} 
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("bookDemo.placeholders.complianceNeeds")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="esg_reporting">{t("bookDemo.complianceTypes.esg")}</SelectItem>
                          <SelectItem value="gdpr">{t("bookDemo.complianceTypes.gdpr")}</SelectItem>
                          <SelectItem value="financial_regulatory">{t("bookDemo.complianceTypes.financial")}</SelectItem>
                          <SelectItem value="industry_standards">{t("bookDemo.complianceTypes.industry")}</SelectItem>
                          <SelectItem value="data_privacy">{t("bookDemo.complianceTypes.privacy")}</SelectItem>
                          <SelectItem value="risk_management">{t("bookDemo.complianceTypes.risk")}</SelectItem>
                          <SelectItem value="multiple">{t("bookDemo.complianceTypes.multiple")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="process_requirements"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("bookDemo.processRequirements")}</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder={t("bookDemo.placeholders.processRequirements")} 
                          className="min-h-[120px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="additional_info"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("bookDemo.message")}</FormLabel>
                      <FormControl>
                        <Textarea placeholder={t("bookDemo.placeholders.message")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col items-center space-y-4">
                <Button 
                  type="submit" 
                  className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white px-8"
                  size="lg"
                  disabled={isSubmitting || showSuccessModal}
                  onClick={() => trackEvent('BookDemo', 'ButtonClick', 'SubmitButton')}
                >
                  {isSubmitting ? t("bookDemo.button.submitting") : t("bookDemo.button.submit")}
                </Button>
                <p className="text-sm text-slate-500 max-w-lg mt-16 text-center">
                  {t("bookDemo.termsNotice")} {' '}
                  <a 
                    href="/terms" 
                    className="text-green-600 hover:underline"
                    onClick={() => trackEvent('BookDemo', 'LinkClick', 'Terms of Service')}
                  >
                    {t("bookDemo.termsLink")}{' '}
                  </a> 
                  {t("bookDemo.andText")}{' '}
                  <a 
                    href="/privacy" 
                    className="text-green-600 hover:underline"
                    onClick={() => trackEvent('BookDemo', 'LinkClick', 'Privacy Policy')}
                  >
                    {t("bookDemo.privacyLink")}
                  </a>.
                </p>
              </div>
            </form>
          </Form>
          
          {/* Success Modal */}
          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => {
              setShowSuccessModal(false);
              form.reset();
            }}
            title={t('bookDemo.notifications.success.title')}
            description={t('bookDemo.notifications.success.description')}
          />
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default BookDemoPage;
